import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useOutletContext } from 'react-router-dom'
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import { Box, Typography } from '@mui/material'
import { CommonButton } from '@/components/common'
import { ResultCard } from '@/components/donation'
import { useGetDriveSessionSingle, useCheckDriveAccess } from '@/hooks'
import {
  formatIsoDateWithWeekday,
  buildResultItem,
  CHAIRPERSON_RETURN_TO,
} from '@/utils'
import { ScheduleDriveView } from '@/components/chairperson/schedule-drive'
import { DonorSchedule } from '@/components/chairperson/donor-schedule'
import { ROUTES } from '@/constants'

const OPERATION_TYPE = 'drives'

export const ChairpersonScheduleDrivePage = () => {
  const navigate = useNavigate()
  const { driveId } = useParams()
  const { selectedAccountId } = useOutletContext()
  const {
    driveSessionSingle,
    getDriveSessionSingle,
    loadingDriveSessionSingle,
  } = useGetDriveSessionSingle()
  const {
    hasDriveAccess,
    checkingDriveAccess,
    driveAccessError,
    checkDriveAccess,
  } = useCheckDriveAccess()

  const [showDonorSchedule, setShowDonorSchedule] = useState(false)

  const handleBack = () => {
    navigate(ROUTES.CHAIRPERSON_DASHBOARD, { state: { tab: 'upcoming' } })
  }

  const backButton = (
    <CommonButton
      variant='text'
      startIcon={<ArrowBackIosNewOutlinedIcon sx={{ fontSize: 14 }} />}
      onClick={handleBack}
      sx={{ mb: 2 }}
    >
      Back to Upcoming Drives
    </CommonButton>
  )

  // Drive info/availability below come from account-agnostic public endpoints, so access must
  // be verified explicitly before revealing any of this drive's data.
  useEffect(() => {
    if (!driveId) return
    checkDriveAccess(driveId)
  }, [driveId])

  useEffect(() => {
    if (!driveId || hasDriveAccess !== true) return
    getDriveSessionSingle({
      operation_id: driveId,
      operation_type: OPERATION_TYPE,
    })
  }, [driveId, hasDriveAccess])

  const driveCalendarDate = driveSessionSingle?.date ?? null
  const driveDateLabel = driveCalendarDate
    ? formatIsoDateWithWeekday(driveCalendarDate)
    : null

  const driveSessionItem = useMemo(() => {
    if (!driveSessionSingle) return null
    return buildResultItem(driveSessionSingle, {})
  }, [driveSessionSingle])

  const handleViewDonors = () => {
    navigate(ROUTES.CHAIRPERSON_DRIVE_DONORS.replace(':driveId', driveId), {
      state: {
        accountId: selectedAccountId,
        driveName: driveSessionItem?.name,
        driveDate: driveSessionSingle?.date,
        fromTab: 'upcoming',
        returnTo: CHAIRPERSON_RETURN_TO.SCHEDULE,
      },
    })
  }

  const handleViewResults = () => {
    navigate(`/chairperson/drive-results/${driveId}`, {
      state: {
        accountId: selectedAccountId,
        fromTab: 'upcoming',
        returnTo: CHAIRPERSON_RETURN_TO.SCHEDULE,
      },
    })
  }

  if (checkingDriveAccess || hasDriveAccess === null) {
    return (
      <Box>
        {backButton}
        <ResultCard item={{}} isClickable={false} loading />
      </Box>
    )
  }

  if (!hasDriveAccess) {
    return (
      <Box>
        {backButton}
        <Typography variant='body1' color='error'>
          {driveAccessError?.response?.status === 403
            ? "You don't have access to this drive."
            : 'Unable to load this drive.'}
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {backButton}
      <Box sx={{ mb: { xs: 2, md: 2.5 } }}>
        <ResultCard
          item={driveSessionItem || {}}
          isClickable={false}
          loading={loadingDriveSessionSingle}
          date={driveDateLabel ?? undefined}
          onViewDonorSchedule={() => setShowDonorSchedule(prev => !prev)}
          isShowingDonorSchedule={showDonorSchedule}
          onViewDonors={handleViewDonors}
          onViewResults={handleViewResults}
        />
      </Box>

      {showDonorSchedule ? (
        <DonorSchedule
          driveId={driveId}
          driveInfo={driveSessionItem}
          driveDate={driveDateLabel}
        />
      ) : (
        <ScheduleDriveView
          driveId={driveId}
          driveDate={driveDateLabel ?? ''}
          selectedAccountId={selectedAccountId}
          onlineBookingAllowed={driveSessionSingle?.online_booking_allowed}
          marketingStartDate={driveSessionSingle?.marketing_start_date}
          loadingDriveInfo={loadingDriveSessionSingle}
        />
      )}
    </Box>
  )
}
